import { clearPortalLocalStorage } from '@rsos/base-utils/clearPortalLocalStorage';
import {
  APPLICATION_AGENT_511,
  ALI_ADMIN,
} from '@rsos/constants/locationApplications';
import {
  SOURCE_TCAPI,
  SOURCE_SMS,
  SOURCE_ADMIN_SMS,
  SOURCE_CITIZEN_LOCATION,
} from '@rsos/constants/locationSources';
import { checkStatus, parseJSON } from '@rsos/utils';
import {
  getAPIHost,
  getCallQueueMaxAge,
  getCallQueueMaxSize,
} from '@rsos/utils/metaTags';
import {
  captureExceptionWithScope,
  clearGlobalScope,
} from '@rsos/utils/sentry';
import { store } from '../store';
import { getProductToken } from '../utils/getProductToken';
import {
  ADD_SAD,
  ADD_LOCATION_TO_QUEUE,
  REMOVE_ALL_NUMS,
  REMOVE_NUM_FROM_QUEUE,
  SET_EXPIRED_TIME,
  FETCH_LOCATION_START,
  FETCH_LOCATION_SUCCESS,
  FETCH_LOCATION_FAILED,
  CLEAR_LOCATION_ERROR,
  FORCED_LOGOUT_FAILED,
  FORCED_LOGOUT_START,
  FORCED_LOGOUT_SUCCESS,
  SET_CALLER_ID,
  SET_INITIALIZED_HERMES_STATE,
  SET_JURISDICTION_RG_ADDRESS_START,
  SET_JURISDICTION_RG_ADDRESS,
  SET_JURISDICTION_RG_ADDRESS_SUCCESS,
  SET_JURISDICTION_RG_ADDRESS_ERROR,
  TOGGLE_NUM_LOCATIONS,
} from './actionTypes';

const baseURL = `${getAPIHost()}`;

export const forcedLogOutStart = () => ({
  type: FORCED_LOGOUT_START,
});

export const forcedLogOutSuccess = () => ({
  type: FORCED_LOGOUT_SUCCESS,
});

export const forcedLogOutFail = () => ({
  type: FORCED_LOGOUT_FAILED,
});

export const forcedLogOut = () => dispatch => {
  dispatch(forcedLogOutStart());
  dispatch(forcedLogOutSuccess());
  clearPortalLocalStorage();
  clearGlobalScope();
  window.location = '/';
};

export const addSAData = message => ({ type: ADD_SAD, message });

export const addLocToQueue = oldLoc => {
  const maxCallQueueSize = getCallQueueMaxSize();
  const maxCallQueueAge = getCallQueueMaxAge() * 1000;

  let callerID = oldLoc.caller_id;

  // Remove `+` from callerID to standardize all sources of info
  callerID = callerID.startsWith('+') ? callerID.slice(1) : callerID;

  const smsSources = new Set([
    SOURCE_SMS,
    SOURCE_TCAPI,
    SOURCE_ADMIN_SMS,
    SOURCE_CITIZEN_LOCATION,
  ]);
  const isSMS =
    smsSources.has(oldLoc.source) ||
    oldLoc.class_of_service_category === 'Text';

  const locationTime = oldLoc.location_time || oldLoc.created_time;
  const callStartTime = oldLoc.call_start_time || oldLoc.created_time;

  const loc = {
    ...oldLoc,
    location_time: locationTime,
    call_start_time: callStartTime,
    caller_id: callerID,
    map_style: {
      marker: {
        latest: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
        old: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
        hover: 'http://maps.google.com/mapfiles/ms/icons/ltblue-dot.png',
      },
      circle: {
        latest: '#04ff00',
        old: '#ff0000',
      },
      infoWindow: {
        num: callerID,
        call_start_time: callStartTime,
      },
    },
    isMapMarkerHovered: false,
    isCallQueueNumberHovered: false,
    isTT911: oldLoc.application === APPLICATION_AGENT_511, // Identifier for the map marker
    isAdmin: oldLoc.application === ALI_ADMIN,
    dataType: 'call',
  };

  return {
    type: ADD_LOCATION_TO_QUEUE,
    loc,
    maxCallQueueSize,
    maxCallQueueAge,
    locationType: isSMS ? 'sms' : 'call',
  };
};

export const addLocToQueueSuccess = loc => {
  const { isLoggedIn } = store.getState().sinatra.user;
  return dispatch => {
    if (isLoggedIn) {
      dispatch(addLocToQueue(loc));
    }
  };
};

export const removeNumFromQueue = callerID => ({
  type: REMOVE_NUM_FROM_QUEUE,
  callerID,
});

export const removeAllNums = () => ({
  type: REMOVE_ALL_NUMS,
});

/**
 * Triggering reversegeocode for Jurisdiction view
 * @param {Array} locationList - location list from the selected number
 */
export const jurisdictionRG =
  (locationList, callerID, dataType) => dispatch => {
    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      let rgAddress;
      dispatch(setRGAddressStart());
      const newList = locationList.map(item => {
        return { ...item };
      });
      newList.forEach((location, index) => {
        let latlng = {
          lat: parseFloat(location.latitude),
          lng: parseFloat(location.longitude),
        };
        if (!location.rgAddress) {
          geocoder.geocode({ location: latlng }, (result, status) => {
            if (status === 'OK') {
              if (result[0]) {
                rgAddress = result[0].formatted_address;
                location.rgAddress = `${rgAddress}`;
              }
              dispatch(setRGAddress(newList, callerID, index, dataType));
              return resolve();
            } else {
              location.rgAddress = `Address not displayed due to large uncertainty radius`;
              return reject(dispatch(setRGAddressError()));
            }
          });
        }
      });
      return resolve(dispatch(setRGAddressSuccess()));
    });
  };

export function setRGAddressStart() {
  return {
    type: SET_JURISDICTION_RG_ADDRESS_START,
  };
}

export function setRGAddressSuccess() {
  return {
    type: SET_JURISDICTION_RG_ADDRESS_SUCCESS,
  };
}

export function setRGAddressError() {
  return {
    type: SET_JURISDICTION_RG_ADDRESS_ERROR,
  };
}

export function setRGAddress(locationList, callerID, index, dataType) {
  return {
    type: SET_JURISDICTION_RG_ADDRESS,
    callerID,
    locationList,
    index,
    dataType,
  };
}

export const setExpiredTime = (callerID, dataType) => {
  // default 30 minutes
  const call_queue_max_age = getCallQueueMaxAge() * 1000;

  let nowTS = Math.round(new Date().getTime());
  return {
    type: SET_EXPIRED_TIME,
    expiredTime: nowTS + call_queue_max_age,
    callerID,
    dataType,
  };
};

export const setCallerID = callerID => ({
  type: SET_CALLER_ID,
  callerID,
});

export const fetchLocationStart = () => ({
  type: FETCH_LOCATION_START,
});

export const fetchLocationSuccess = payload => ({
  type: FETCH_LOCATION_SUCCESS,
  payload,
});

export const fetchLocationFailed = error => ({
  type: FETCH_LOCATION_FAILED,
  error: error.status,
});

export const clearLocationError = () => ({
  type: CLEAR_LOCATION_ERROR,
});

export const fetchLocation = (callerID, token) => dispatch => {
  dispatch(fetchLocationStart());
  dispatch(setCallerID(callerID));
  const { sinatra } = store.getState();
  const { irpVersion } = sinatra.irp;
  return fetch(`${baseURL}/v1/rsos/location?caller_id=${callerID}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-RapidSOS-iRPVersion': irpVersion,
    },
    credentials: 'same-origin',
  })
    .then(checkStatus)
    .then(response => {
      return parseJSON(response).then(res => ({
        requestID: response.headers.get('X-RapidSOS-Request-ID'),
        res,
      }));
    })
    .then(({ requestID, res }) => {
      let newLocation = { ...res.results[0] };

      if (newLocation.source && typeof newLocation.source === 'string') {
        newLocation.source = newLocation.source.toLowerCase();
      }
      if (newLocation.meta && requestID) {
        newLocation.meta.request_id = requestID;
      }

      dispatch(addLocToQueueSuccess(newLocation));
      const payloadID = res.results[0]?.meta?.payload_id;
      const response = {
        action: dispatch(fetchLocationSuccess({ callerID })),
        requestID,
        payloadID,
      };
      return response;
    })
    .catch(err => {
      if (err.status !== 404) {
        const { sinatra } = store.getState();
        const { tokenExpiration, refreshExpiration } = sinatra.auth.tokens;
        const { productToken, productTokenExpiration } =
          getProductToken(sinatra);
        captureExceptionWithScope(err, {
          extra: {
            accessTokenExpiration: tokenExpiration,
            bearerToken: productToken,
            bearerTokenExpiration: productTokenExpiration,
            refreshTokenExpiration: refreshExpiration,
          },
        });
      }
      return dispatch(fetchLocationFailed(err));
    });
};

export const setInitializedHermesState = bool => ({
  type: SET_INITIALIZED_HERMES_STATE,
  bool,
});

export const toggleNumLocations = num => ({
  type: TOGGLE_NUM_LOCATIONS,
  num,
});
